import React, { useState } from 'react'
import { LuCircleAlert } from 'react-icons/lu'
import { Link, Navigate, Outlet, useNavigate } from 'react-router'

import RqLogo from '@/assets/img/logo.svg'
import LayoutWrapper from '@/components/layouts/LayoutWrapper'
import OfficeAccountMenu from '@/components/layouts/OfficeLayout/components/OfficeAccountMenu'
import { Alert, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import LoadingWidget from '@/components/widgets/LoadingWidget'
import { useAppStateQuery } from '@/hooks/useAppStateQuery'
import { useAuth } from '@/hooks/useAuth'
import { useIsRoute } from '@/hooks/useIsRoute'
import { useLongPress } from '@/hooks/useLongPress'
import { useOfficeContext } from '@/hooks/useOfficeContext'
import { isDevelopment } from '@/lib/helpers/envHelpers'
import { isOffice } from '@/lib/helpers/officeHelpers'
import { ROUTES } from '@/lib/routes'
import { cn } from '@/lib/utils'
import DiagnosticModal from '@/pages/officeTaskPane/components/DiagnosticModal'

const OfficeLayout: React.FC = () => {
  const { isAuthenticated, isCompanyUser, isCurrentCompanyDomainLoginGranted } =
    useAuth()
  const office = useOfficeContext()
  const isPageEnabled = isDevelopment() || isOffice()
  const navigate = useNavigate()
  const [showDiagnosticModal, setShowDiagnosticModal] = useState(false)
  const isRoute = useIsRoute()
  const appStateQuery = useAppStateQuery()

  const longPressProps = useLongPress({
    onLongPress: () => setShowDiagnosticModal(true),
    duration: 5000,
  })

  if (!isPageEnabled) {
    return (
      <Alert variant="warning" className="my-4">
        <LuCircleAlert />

        <AlertTitle>
          This add-in is only available in the Outlook client.
        </AlertTitle>
      </Alert>
    )
  }

  if (!office.isInitialized || appStateQuery.isLoading) {
    return <LoadingWidget />
  }

  if (isCompanyUser && !isCurrentCompanyDomainLoginGranted) {
    return <Navigate to={ROUTES.domainAccessDenied()} replace />
  }

  const isBluePage = isRoute(ROUTES._officeConnectEmailAndCalendar)

  return (
    <LayoutWrapper
      className={cn('OfficeLayout h-full', isBluePage ? '' : 'bg-white')}
    >
      <div className="w-full">
        {isAuthenticated && (
          <header
            className={cn(
              'box-shadow-lg flex w-full justify-between gap-4 bg-white p-4',
              isBluePage ? '' : 'mb-6',
            )}
          >
            <Link to={ROUTES.officeTaskPane()}>
              <img
                src={RqLogo}
                alt="RQ Logo"
                className="relative"
                style={{ height: '40px', maxWidth: '45px', top: '-3px' }}
                {...longPressProps}
              />
            </Link>

            <div className="flex flex-col items-end">
              <OfficeAccountMenu />

              <Button
                variant="plain"
                className="text-xs"
                onClick={() => {
                  const url = ROUTES.signOut({
                    searchParams: { redirectUri: ROUTES.officeTaskPane() },
                  })

                  navigate(url)
                }}
              >
                Sign out
              </Button>
            </div>
          </header>
        )}

        <div className="mx-2">
          <Outlet />
        </div>
      </div>

      <DiagnosticModal
        show={showDiagnosticModal}
        onHide={() => setShowDiagnosticModal(false)}
      />
    </LayoutWrapper>
  )
}

export default OfficeLayout
